import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import { SEO } from "../components/shared/SEO";
import { Reveal, ImageReveal } from "../components/shared/Reveal";
import { BeforeAfter } from "../components/shared/BeforeAfter";
import { CTABand } from "../components/shared/Sections";
import { api, imgUrl, isPdf, STATUS_LABELS, fmtEur, fmtPct } from "../lib/api";

const Meta = ({ label, value, testId }) => value ? (
  <div data-testid={testId}>
    <div className="label-line">{label}</div>
    <div className="font-serif text-2xl md:text-3xl font-light text-ink">{value}</div>
  </div>
) : null;

const Gallery = ({ title, eyebrow, items, cols = "md:grid-cols-2" }) => items?.length ? (
  <section className="section bg-white">
    <div className="container-hh">
      <Reveal className="mb-12"><div className="eyebrow mb-4">{eyebrow}</div><h2 className="h-section">{title}</h2></Reveal>
      <div className={`grid grid-cols-1 ${cols} gap-6 md:gap-8`}>
        {items.map((src, i) => isPdf(src) ? (
          <a key={i} href={imgUrl(src)} target="_blank" rel="noreferrer" data-testid={`floorplan-pdf-${i}`} className="flex items-center gap-4 border border-ink/10 p-8 hover:border-gold transition-colors">
            <FileText strokeWidth={1} size={32} /><span className="text-sm uppercase tracking-[0.18em]">Planimetria {i + 1} (PDF)</span>
          </a>
        ) : (
          <ImageReveal key={i} src={imgUrl(src)} alt={`${title} ${i + 1}`} className={`${i % 3 === 0 ? "md:col-span-2 aspect-[16/9]" : "aspect-[4/3]"} bg-beige`} />
        ))}
      </div>
    </div>
  </section>
) : null;

export default function ProgettoDetail() {
  const { slug } = useParams();
  const { data: p, isLoading, error } = useQuery({ queryKey: ["project", slug], queryFn: () => api.get(`/projects/${slug}`).then((r) => r.data) });

  if (isLoading) return <div className="min-h-screen bg-ink" />;
  if (error || !p) return (
    <section className="min-h-[70vh] bg-ink text-cream flex items-center"><div className="container-hh pt-32"><h1 className="h-display">Progetto non trovato.</h1><Link to="/progetti" className="btn-light mt-10">Tutti i progetti</Link></div></section>
  );

  const before = p.before_images?.[0], after = p.after_images?.[0] || p.cover_image;

  return (
    <>
      <SEO title={p.seo_title || `${p.title.trim()}, ${p.city} | Progetto Home Haven`} description={p.seo_description || `${STATUS_LABELS[p.status]}. ${p.summary}`.slice(0, 160)} image={imgUrl(p.cover_image)} />
      {p.cover_full_graphic ? (
      <section data-testid="project-hero-graphic">
        <div className="bg-ink pt-28 md:pt-36 pb-12 md:pb-16">
          <div className="container-hh">
            <nav aria-label="Breadcrumb" data-testid="project-breadcrumb" className="mb-6 flex flex-wrap items-center gap-2 text-xs uppercase tracking-[0.2em] text-cream/70"><Link to="/" className="hover:text-gold transition-colors">Home</Link><span aria-hidden="true">/</span><Link to="/progetti" data-testid="project-back-link" className="hover:text-gold transition-colors">Progetti</Link><span aria-hidden="true">/</span><span className="text-cream" aria-current="page">{p.title}</span></nav>
            <Reveal><img src={imgUrl(p.cover_image)} alt={`${p.title} — ${p.city}`} className="w-full h-auto shadow-2xl shadow-black/40" fetchPriority="high" /></Reveal>
          </div>
        </div>
        <div className="container-hh py-14 md:py-20">
          <Reveal>
            <div className="eyebrow mb-5">{p.city} — {STATUS_LABELS[p.status]}</div>
            <h1 className="h-section" data-testid="project-title">{p.title}</h1>
            <p className="lead mt-8 max-w-2xl">{p.summary}</p>
          </Reveal>
        </div>
      </section>
      ) : (
      <section className="relative min-h-[85vh] flex items-end bg-ink text-cream overflow-hidden">
        <img src={imgUrl(p.cover_image)} alt={`${p.title} — ${p.city}`} className="absolute inset-0 img-cover" fetchPriority="high" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-black/20" />
        <div className="container-hh relative pb-20 pt-48">
          <nav aria-label="Breadcrumb" data-testid="project-breadcrumb" className="mb-10 flex flex-wrap items-center gap-2 text-xs uppercase tracking-[0.2em] text-cream/70"><Link to="/" className="hover:text-gold transition-colors">Home</Link><span aria-hidden="true">/</span><Link to="/progetti" data-testid="project-back-link" className="hover:text-gold transition-colors">Progetti</Link><span aria-hidden="true">/</span><span className="text-cream" aria-current="page">{p.title}</span></nav>
          <Reveal>
            <div className="eyebrow mb-5">{p.city} — {STATUS_LABELS[p.status]}</div>
            <h1 className="h-display" data-testid="project-title">{p.title}</h1>
            <p className="lead !text-cream/80 mt-8 max-w-2xl">{p.summary}</p>
          </Reveal>
        </div>
      </section>
      )}

      <section className="section bg-cream">
        <div className="container-hh grid grid-cols-1 lg:grid-cols-12 gap-14">
          <Reveal className="lg:col-span-4 grid grid-cols-2 lg:grid-cols-1 gap-10 content-start">
            <Meta label="Città" value={p.city} testId="project-meta-city" />
            <Meta label="Stato" value={STATUS_LABELS[p.status]} testId="project-meta-status" />
            <Meta label="Superficie" value={p.surface_sqm ? `${p.surface_sqm} mq` : null} testId="project-meta-surface" />
            <Meta label="Anno" value={p.year} testId="project-meta-year" />
            <Meta label="Strategia" value={p.strategy} testId="project-meta-strategy" />
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7 lg:col-start-6">
            <div className="eyebrow mb-6">Il progetto</div>
            {p.description.split("\n").filter(Boolean).map((para, i) => <p key={i} className="lead mb-5" data-testid={`project-description-${i}`}>{para}</p>)}
            <div className="flex flex-wrap gap-x-8 gap-y-3 mt-8">
              {p.status === "concluso" && <Link to="/cosa-facciamo" data-testid="project-link-metodo" className="btn-link">Come lavoriamo</Link>}
              <Link to="/progetti" data-testid="project-link-progetti" className="btn-link">Tutti i progetti</Link>
              <Link to="/proponi-immobile" data-testid="project-link-proponi" className="btn-link">Proponi un immobile</Link>
            </div>
          </Reveal>
        </div>
      </section>

      {before && after && (
        <section className="section bg-white">
          <div className="container-hh">
            <Reveal className="mb-12"><div className="eyebrow mb-4">Before / After</div><h2 className="h-section">La trasformazione.</h2></Reveal>
            <Reveal><BeforeAfter before={imgUrl(before)} after={imgUrl(after)} /></Reveal>
          </div>
        </section>
      )}

      <Gallery eyebrow="Rendering" title="Il progetto immaginato." items={p.renders} />
      <Gallery eyebrow="Gallery" title="Gli spazi trasformati." items={p.gallery} />
      <Gallery eyebrow="Planimetrie" title="La distribuzione degli spazi." items={p.floor_plans} />

      {(p.roi || p.economics?.length > 0) && (
        <section className="section bg-cream">
          <div className="container-hh grid grid-cols-1 lg:grid-cols-12 gap-12">
            <Reveal className="lg:col-span-4">
              <div className="eyebrow mb-4">Dati economici</div><h2 className="h-section">I numeri dell'operazione.</h2>
              {p.roi && <p className="text-sm font-light text-ink/60 mt-6">ROI calcolato come margine lordo sul capitale investito (acquisto + trasformazione). Dati indicativi, al netto di imposte e oneri finanziari.</p>}
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-7 lg:col-start-6">
              {p.roi && (
                <div className="grid grid-cols-2 gap-6 mb-10" data-testid="project-roi-highlight">
                  <div className="bg-ink text-cream p-8"><div className="text-[11px] uppercase tracking-[0.2em] text-gold">ROI operazione</div><div className="font-serif text-5xl md:text-6xl font-light mt-3" data-testid="project-roi-pct">{fmtPct(p.roi.roi_pct)}</div></div>
                  <div className="bg-white border border-ink/10 p-8"><div className="text-[11px] uppercase tracking-[0.2em] text-stone">ROI annualizzato</div><div className="font-serif text-5xl md:text-6xl font-light mt-3 text-sage">{p.roi.annualized_roi_pct != null ? fmtPct(p.roi.annualized_roi_pct) : "—"}</div>{p.roi.duration_months && <div className="text-xs text-stone mt-2">Durata cantiere: {p.roi.duration_months} mesi</div>}</div>
                </div>
              )}
              <table className="w-full" data-testid="project-economics">
                <tbody>
                  {p.roi && [
                    ["Acquisto immobile (incl. oneri accessori)", fmtEur(p.roi.purchase_cost)],
                    ["Spese di trasformazione", fmtEur(p.roi.renovation_cost)],
                    ["Investimento totale", fmtEur(p.roi.total_investment), true],
                    ["Valore immobile finito", fmtEur(p.roi.final_value)],
                    ["Margine lordo", fmtEur(p.roi.margin), true],
                  ].map(([l, v, strong]) => (
                    <tr key={l} className={`border-b border-sage/20 ${strong ? "bg-white" : ""}`}>
                      <td className="py-5 px-2 text-xs uppercase tracking-[0.2em] text-stone">{l}</td>
                      <td className={`py-5 px-2 text-right font-serif text-2xl md:text-3xl ${strong ? "font-medium" : "font-light"}`}>{v}</td>
                    </tr>
                  ))}
                  {p.economics.length > 0 && <tr><td colSpan={2} className="pt-8 pb-3 px-2 text-[10px] uppercase tracking-[0.25em] text-gold">Dettaglio voci</td></tr>}
                  {p.economics.map((e, i) => (
                    <tr key={i} className="border-b border-sage/20">
                      <td className="py-5 px-2 text-xs uppercase tracking-[0.2em] text-stone">{e.label}</td>
                      <td className="py-5 px-2 text-right font-serif text-2xl md:text-3xl font-light">{e.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Reveal>
          </div>
        </section>
      )}

      <CTABand dark eyebrow="Il prossimo progetto" title="Vuoi collaborare con Home Haven?" primary={{ to: "/investitori", label: "Investi con noi", testId: "project-cta-investi" }} secondary={{ to: "/proponi-immobile", label: "Proponi un immobile", testId: "project-cta-proponi" }} />
    </>
  );
}
